import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { getAuthHeader } from "../lib/kds";

/** Quanto tempo a última ação fica esperando o Desfazer (dono, 03/10/2026). */
export const SEGUNDOS_DESFAZER = 10;

/** Envio que sobrevive ao fechamento da página (`keepalive`): usado quando a
 *  ação passa a valer porque a tela foi escondida ou fechada. O erro sai no
 *  mesmo formato do axios, que é o que os avisos de cada ação leem. */
async function enviarQueSobrevive(r) {
  const resp = await fetch(r.url, {
    method: r.metodo.toUpperCase(), keepalive: true,
    headers: { ...getAuthHeader().headers, "Content-Type": "application/json" },
    body: JSON.stringify(r.corpo || {}),
  });
  if (resp.ok) return;
  let data = null;
  try { data = await resp.json(); } catch (_) { /* resposta sem corpo */ }
  const err = new Error(`HTTP ${resp.status}`);
  err.response = { status: resp.status, data };
  throw err;
}

/** Desfazer a última ação do KDS.
 *
 *  Decisão do dono (03/10/2026): a ação só VALE quando o tempo acaba. Nesta
 *  tela ela aparece na hora; o garçom, o WhatsApp do cliente, o entregador e o
 *  caixa só ficam sabendo 10 s depois — ou antes, se a pessoa fizer outra ação
 *  (só a última tem volta). Desfazer é como se o toque não tivesse existido:
 *  nada foi enviado, nada precisa ser corrigido no servidor.
 *
 *  `pendente` = a ação em espera (a da barra). `gravando` = as que já estão
 *  saindo pro servidor: continuam desenhadas até a lista nova chegar, senão a
 *  comanda piscaria de volta pro lugar antigo.
 *
 *  A espera acaba antes do tempo quando ninguém mais está olhando o botão:
 *  aba escondida, tablet bloqueado, saída da tela do KDS ou fechamento do
 *  aplicativo — perder a marcação de um prato é pior do que não poder desfazer.
 *
 *  `ativo` = a chave da loja em Preferências. Desligada, tudo grava na hora,
 *  como era antes desta função.
 */
export default function useDesfazer({ ativo, recarregar }) {
  const [pendente, setPendente] = useState(null);
  const [gravando, setGravando] = useState([]);
  const pendenteRef = useRef(null);
  const emCursoRef = useRef([]);     // ações com requisição ainda por sair (pra fechar a tela)
  const relogioRef = useRef(null);
  const filaRef = useRef(Promise.resolve());
  const seqRef = useRef(0);
  const vivoRef = useRef(true);
  const recarregarRef = useRef(recarregar);
  recarregarRef.current = recarregar;

  /** Uma requisição por vez, na ordem: se uma falhar (comanda editada no
   *  meio), as outras não ficam num estado que ninguém viu. `_proxima` = a
   *  que vai sair; `_feitas` = as que o servidor já respondeu. */
  const enviar = async (acao) => {
    while (acao._proxima < acao.requisicoes.length) {
      const r = acao.requisicoes[acao._proxima];
      acao._proxima += 1;
      r._sobrevive = !!acao._telaSaindo;
      // eslint-disable-next-line no-await-in-loop
      if (r._sobrevive) await enviarQueSobrevive(r);
      // eslint-disable-next-line no-await-in-loop
      else await axios({ method: r.metodo, url: r.url, data: r.corpo, ...getAuthHeader() });
      acao._feitas += 1;
    }
  };

  const efetivar = useCallback((acao, { naHora = false, telaSaindo = false } = {}) => {
    if (telaSaindo) acao._telaSaindo = true;
    if (pendenteRef.current && pendenteRef.current.id === acao.id) {
      clearTimeout(relogioRef.current);
      pendenteRef.current = null;
      setPendente(null);
    }
    emCursoRef.current = [...emCursoRef.current, acao];
    setGravando((g) => [...g, acao]);
    filaRef.current = filaRef.current.then(async () => {
      try {
        await enviar(acao);
        if (acao.aoGravar) acao.aoGravar();
        else if (naHora && acao.sucesso) toast.success(acao.sucesso);
        window.dispatchEvent(new Event("pedidosUpdated"));
      } catch (err) {
        const d = err.response?.data?.detail;
        if (acao.aoFalhar) acao.aoFalhar(err);
        else toast.error(typeof d === "string" ? d : acao.erro);
      }
      emCursoRef.current = emCursoRef.current.filter((x) => x.id !== acao.id);
      try { if (acao.depois) await acao.depois(); } catch (_) { /* só atualiza a tela */ }
      try { await recarregarRef.current(); } catch (_) { /* a próxima leitura corrige */ }
      if (vivoRef.current) setGravando((g) => g.filter((x) => x.id !== acao.id));
    });
    return filaRef.current;
  }, []);

  /** Toda ação do KDS entra por aqui. */
  const agendar = useCallback((descricao) => {
    const anterior = pendenteRef.current;
    // Tocou no "Desfazer" do próprio item que ainda estava em espera: é o
    // mesmo que o Desfazer da barra — nada foi enviado, nada a corrigir.
    if (anterior && descricao.inversaDe && descricao.inversaDe === anterior.chave) {
      clearTimeout(relogioRef.current);
      pendenteRef.current = null;
      setPendente(null);
      return undefined;
    }
    const acao = { ...descricao, id: ++seqRef.current, _proxima: 0, _feitas: 0 };
    if (anterior) efetivar(anterior);            // só a última ação tem volta
    if (!ativo) return efetivar(acao, { naHora: true });
    acao.ate = Date.now() + SEGUNDOS_DESFAZER * 1000;
    pendenteRef.current = acao;
    setPendente(acao);
    clearTimeout(relogioRef.current);
    relogioRef.current = setTimeout(() => efetivar(acao), SEGUNDOS_DESFAZER * 1000);
    return undefined;
  }, [ativo, efetivar]);

  const desfazer = useCallback(() => {
    if (!pendenteRef.current) return;
    clearTimeout(relogioRef.current);
    pendenteRef.current = null;
    setPendente(null);
    toast("Ação desfeita", { duration: 2000 });
  }, []);

  // A loja desligou a chave com uma ação em espera: ela passa a valer.
  useEffect(() => {
    if (!ativo && pendenteRef.current) efetivar(pendenteRef.current);
  }, [ativo, efetivar]);

  useEffect(() => {
    // Escondeu (tablet bloqueado, outra aba, começo do fechamento): a ação
    // vale agora, e o envio já sai do jeito que sobrevive à página.
    const aoEsconder = () => {
      if (document.visibilityState !== "hidden") return;
      emCursoRef.current.forEach((a) => { a._telaSaindo = true; });
      if (pendenteRef.current) efetivar(pendenteRef.current, { telaSaindo: true });
    };
    // Fechando a aba/o aplicativo: o que ainda não saiu vai com `keepalive`,
    // que o navegador entrega mesmo depois de a página morrer. A requisição
    // que já estava no ar só é repetida se repetir não estraga (marcar item);
    // mudança de etapa e chamada de entregador nunca vão duas vezes.
    const aoSair = () => {
      const filaDeSaida = [...emCursoRef.current, ...(pendenteRef.current ? [pendenteRef.current] : [])];
      filaDeSaida.forEach((acao) => {
        acao._telaSaindo = true;
        acao.requisicoes.forEach((r, n) => {
          const respondida = n < acao._feitas;
          const noAr = !respondida && n < acao._proxima;
          if (respondida || (noAr && (r._sobrevive || !r.repetivel))) return;
          enviarQueSobrevive(r).catch(() => { /* página fechando: não há a quem avisar */ });
        });
        acao._proxima = acao.requisicoes.length;
      });
      clearTimeout(relogioRef.current);
      pendenteRef.current = null;
      // A página pode voltar do cache do navegador: sem barra parada na tela.
      if (vivoRef.current) setPendente(null);
    };
    document.addEventListener("visibilitychange", aoEsconder);
    window.addEventListener("pagehide", aoSair);
    return () => {
      document.removeEventListener("visibilitychange", aoEsconder);
      window.removeEventListener("pagehide", aoSair);
    };
  }, [efetivar]);

  // Saiu da tela do KDS (voltou pro painel): a ação em espera vale agora.
  useEffect(() => {
    vivoRef.current = true;
    return () => {
      vivoRef.current = false;
      clearTimeout(relogioRef.current);
      if (pendenteRef.current) efetivar(pendenteRef.current);
    };
  }, [efetivar]);

  /** A fila como ESTA tela deve mostrar: o que o servidor mandou, mais o que
   *  está gravando e a ação em espera. */
  const aplicar = useCallback(
    (pedidos) => [...gravando, ...(pendente ? [pendente] : [])].reduce((lista, a) => a.aplicar(lista), pedidos),
    [gravando, pendente],
  );

  return { pendente, agendar, desfazer, aplicar };
}
